import { useLayoutEffect, useState } from "react";

import { cn } from "../../lib/utils";

import BrandSideCart from "./BrandSideCart";
import GlobalSideCartBrandList, {
  type GlobalSideCartBrand,
} from "./GlobalSideCartBrandList";
import GlobalSideCartMiniView, {
  GLOBAL_SIDE_CART_PANEL_ID,
} from "./GlobalSideCartMiniView";

export type GlobalSideCartVariant = "push" | "float";
export type GlobalSideCartCollapsedMode = "hidden" | "mini";

export interface GlobalSideCartPanelProps {
  open: boolean;
  variant: GlobalSideCartVariant;
  brands: GlobalSideCartBrand[];
  /** Currently open individual brand cart, or null for the multi-brand list. */
  selectedBrand: string | null;
  onSelectBrand: (brandName: string) => void;
  onBackToList: () => void;
  onClose: () => void;
  onChangeQuantity: (brandName: string, token: string, quantity: number) => void;
  onRemove: (brandName: string, token: string) => void;
  /** Order minimum in dollars applied to every brand's cart. Defaults to 150. */
  minimum?: number;
  /**
   * What the panel shows when `open` is false.
   * - "hidden" (default): width 0 / slides off-screen, matching the FAB entry points.
   * - "mini": a 64px avatar rail stays on screen (hidden only when the cart is empty).
   *   PUSH grows that rail in place on click; FLOAT keeps the rail and overlays the
   *   expanded panel on hover/click.
   */
  collapsedMode?: GlobalSideCartCollapsedMode;
  /** Click on the mini rail — PUSH expands on click; FLOAT click also expands. */
  onMiniClick?: () => void;
  /**
   * FLOAT hover intent. The caller debounces; PUSH should omit these so the
   * mini rail is click-only, matching the PoC.
   */
  onHoverEnter?: () => void;
  onHoverLeave?: () => void;
  /** Token of the line to tint — the item that was just added. */
  highlightedItemToken?: string;
  /** When it returns a path for a given brand, that brand's cart header name links there (e.g. to its storefront page), passing that brand's current cart items along so the destination can hand off the cart contents too. Returning undefined keeps plain text. */
  getBrandHref?: (
    brandName: string,
    items: GlobalSideCartBrand["items"]
  ) => string | undefined;
  /** Extra className applied to every product/brand `<img>` rendered inside (e.g. a tint overlay). */
  imageOverlayClassName?: string;
}

/**
 * Positions the global sidecart per the active variant:
 * - "push": a normal sticky flow child (page content shrinks beside it) — the
 *   parent page is expected to render this as a flex sibling of the rest of
 *   the page (nav included), so the whole page pushes over.
 * - "float": fixed to the viewport, overlapping page content with no
 *   dimming scrim. Pinned under the nav (measured from the rendered
 *   <header>) — except when paired with the "mini" collapsed mode (right
 *   mini-rail), where it spans the full viewport height to match the rail.
 *
 * Stays mounted in both states — only `open` toggles a width/transform
 * transition — so it slides in/out at the same 150ms pace as the CartFab
 * instead of popping in/out instantly.
 *
 * When `collapsedMode` is "mini", a collapsed avatar rail stays reserved so
 * closing the cart does not drop the page back to full width.
 */
export default function GlobalSideCartPanel({
  open,
  variant,
  brands,
  selectedBrand,
  onSelectBrand,
  onBackToList,
  onClose,
  onChangeQuantity,
  onRemove,
  minimum = 150,
  collapsedMode = "hidden",
  onMiniClick,
  onHoverEnter,
  onHoverLeave,
  highlightedItemToken,
  getBrandHref,
  imageOverlayClassName,
}: GlobalSideCartPanelProps) {
  // Right mini-rail (collapsedMode "mini") expands full-height instead of
  // pinning below the nav — the rail itself already spans the full viewport,
  // so the expanded panel should match it rather than leaving a gap up top.
  const isFullHeightFloat = variant === "float" && collapsedMode === "mini";
  const floatTop = useFloatCartTop(variant === "float" && !isFullHeightFloat);
  const expandedTop = isFullHeightFloat ? 0 : floatTop;
  const showMiniRail = collapsedMode === "mini" && brands.length > 0;
  const usesHoverIntent = showMiniRail && variant === "float";

  const selected = brands.find((brand) => brand.brandName === selectedBrand);

  const expandedContent = selected ? (
    <BrandSideCart
      brandName={selected.brandName}
      minimum={minimum}
      items={selected.items}
      onBack={onBackToList}
      onClose={onClose}
      onChangeQuantity={(token, quantity) =>
        onChangeQuantity(selected.brandName, token, quantity)
      }
      onRemove={(token) => onRemove(selected.brandName, token)}
      highlightedItemToken={highlightedItemToken}
      brandHref={getBrandHref?.(selected.brandName, selected.items)}
      imageOverlayClassName={imageOverlayClassName}
    />
  ) : (
    <GlobalSideCartBrandList
      brands={brands}
      onSelectBrand={onSelectBrand}
      onClose={onClose}
      minimum={minimum}
      imageOverlayClassName={imageOverlayClassName}
    />
  );

  const miniView = (
    <GlobalSideCartMiniView
      brands={brands}
      selectedBrand={selectedBrand}
      isExpanded={open}
      onExpand={onMiniClick ?? (() => {})}
      onMouseEnter={usesHoverIntent ? onHoverEnter : undefined}
      onMouseLeave={usesHoverIntent ? onHoverLeave : undefined}
    />
  );

  if (variant === "push") {
    let widthClass = "w-0";
    if (open) {
      widthClass = "w-[320px] xl:w-[360px]";
    } else if (showMiniRail) {
      widthClass = "w-16";
    }

    return (
      <div
        id={GLOBAL_SIDE_CART_PANEL_ID}
        className={cn(
          "sticky top-0 h-screen shrink-0 overflow-hidden transition-[width] duration-150 ease-out",
          widthClass
        )}
      >
        {showMiniRail && !open ? miniView : expandedContent}
      </div>
    );
  }

  return (
    <>
      {showMiniRail ? (
        <div className="sticky top-0 h-screen w-16 shrink-0">{miniView}</div>
      ) : null}
      <div
        id={GLOBAL_SIDE_CART_PANEL_ID}
        className={cn(
          "fixed right-0 z-600 shadow-[0_0_16px_rgba(0,0,0,0.15)] transition-transform duration-150 ease-out",
          open ? "translate-x-0" : "pointer-events-none translate-x-full"
        )}
        style={{ top: expandedTop, height: `calc(100vh - ${expandedTop}px)` }}
        onMouseEnter={usesHoverIntent ? onHoverEnter : undefined}
        onMouseLeave={usesHoverIntent ? onHoverLeave : undefined}
      >
        {expandedContent}
      </div>
    </>
  );
}

/**
 * Tracks the rendered <header>'s live bottom edge (viewport-relative), not just
 * its height, so the floating panel follows the nav's actual on-screen position
 * as the page scrolls. The playground's mock nav doesn't auto-hide/show like
 * production's does, and some page layouts don't keep it pinned via `sticky`
 * either — measuring once on mount would leave a stale gap (or an overlap) once
 * the nav scrolls away. Recomputing on every scroll keeps the panel's top glued
 * to wherever the nav currently ends, collapsing to 0 once it's fully scrolled
 * out of view.
 */
export function useFloatCartTop(enabled: boolean): number {
  const [top, setTop] = useState(0);

  useLayoutEffect(() => {
    if (!enabled) return;
    const header = document.querySelector("header");
    if (!header) return;

    const update = () =>
      setTop(Math.max(0, header.getBoundingClientRect().bottom));
    update();

    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const observer = new ResizeObserver(update);
    observer.observe(header);

    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      observer.disconnect();
    };
  }, [enabled]);

  return top;
}
